import { useContext } from 'react'

import { digObject, formatDate, launchModal } from '@domain-group/javascript-utils'

import { useLatestEntity, useThunkDispatch } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import type { CampaignModel, CampaignPortalSyncModel, PortalModel } from '@models/types'

import * as campaignPortalSyncActions from '@redux/modules/campaignPortalSync'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

export type GenerateUrlsOptions = {
  prefixRoute: string,
}

export const generateUrls = (options?: GenerateUrlsOptions) => {
  const { prefixRoute = 'systemManager' } = options || {}

  return {
    campaignPortalSyncsOverview: `#/${prefixRoute}/overview/portalSyncs`,
    campaignPortalSyncsExportOverview: `#/${prefixRoute}/overview/portalSyncs?sync_type=export`,
    campaignPortalSyncsImportOverview: `#/${prefixRoute}/overview/portalSyncs?sync_type=import`,
  }
}

type CampaignPortalSyncFetchLogParams = {
  campaignPortalSync: CampaignPortalSyncModel,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const campaignPortalSyncFetchLog = (params: CampaignPortalSyncFetchLogParams) => {
  const { campaignPortalSync, dispatch, requestOptions } = params
  const { fetchLog } = campaignPortalSyncActions

  return dispatch(fetchLog(campaignPortalSync, requestOptions))
}

export type ShowCampaignPortalSyncDetailsModalPayload = {
  campaignPortalSync: Partial<CampaignPortalSyncModel>,
}

type ManageCampaignPortalSyncDetailsPayload = {
  campaignPortalSync: CampaignPortalSyncModel,
  showCampaignPortalSyncDetailsModal: (payload: ShowCampaignPortalSyncDetailsModalPayload) => void,
}

const manageCampaignPortalSyncDetails = (
  params: ManageCampaignPortalSyncDetailsPayload,
) => new Promise((resolve, reject) => {
  const { campaignPortalSync, showCampaignPortalSyncDetailsModal } = params

  if (showCampaignPortalSyncDetailsModal){
    const payload = {
      campaignPortalSync,
    }

    showCampaignPortalSyncDetailsModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCampaignPortalSyncDetailsModal not defined in PageContext callbacks'))
})

export function useRelations(campaignPortalSync: Partial<CampaignPortalSyncModel>) {
  const { campaign_id, portal_id } = campaignPortalSync || {}

  const { campaigns, portals } = useSelector(reduxState => reduxState.entities)

  const campaign: Partial<CampaignModel> = campaign_id && campaigns[campaign_id] ? campaigns[campaign_id] : {}
  const portal: Partial<PortalModel> = portal_id ? portals[portal_id] || {} : {}

  return {
    campaign,
    portal,
  }
}

function useCampaignPortalSync(initEntity: Partial<CampaignPortalSyncModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: campaignPortalSync } = useLatestEntity(initEntity, 'campaignPortalSyncs')

  const { callbacks } = useContext(PageContext)
  const { showCampaignPortalSyncDetailsModal } = callbacks || {}

  const dispatch = useThunkDispatch()

  // @ts-expect-error TS2345
  const relations = useRelations(campaignPortalSync)
  const { campaign, portal } = relations

  const lastSyncDate = digObject(campaignPortalSync, 'dates.synced.date_time')
  const lastSyncDateStr = lastSyncDate ? formatDate(lastSyncDate, 'dd/MM/yyyy HH:mm', 'dd/MM/yyyy HH:mma') : null

  const errorMessage = digObject(campaignPortalSync, 'log_data.listings.0.errors')
  const listingId = digObject(campaignPortalSync, 'log_data.listings.0.listing_id')

  return {
    callbacks: {
      campaignPortalSyncFetchLog: (_: unknown, requestOptions: EntityOptions) => (
        // @ts-expect-error TS2322
        campaignPortalSyncFetchLog({ campaignPortalSync, dispatch, requestOptions })
      ),
      manageCampaignPortalSyncDetails: () => (
        // @ts-expect-error TS2322
        manageCampaignPortalSyncDetails({ campaignPortalSync, showCampaignPortalSyncDetailsModal })
      ),
      viewPortalSyncDetails: () => launchModal({
        callbacks,
        modalKey: 'CampaignPortalSyncDetailsModal',
        payload: { campaignPortalSync },
      }),
    },
    campaign,
    campaignPortalSync,
    errorMessage,
    lastSyncDateStr,
    listingId,
    portal,
    urls: generateUrls(),
  }
}

export default useCampaignPortalSync
